/**
 * OpenAI-compatible chat completion — direct or via server proxy.
 * Used by the test-connection flow and as a fallback when the agent backend
 * is unavailable (plain chat, no tools).
 */
import { ChatMessage, ChatOptions, LLMConfig } from "./types";

function endpoint(baseUrl: string): string {
  const base = baseUrl.replace(/\/+$/, "");
  return `${base}/chat/completions`;
}

function headers(cfg: LLMConfig): Record<string, string> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (cfg.apiKey) h["Authorization"] = `Bearer ${cfg.apiKey}`;
  return h;
}

// When a Horsir server is available (same origin), a failed direct call (often a
// browser CORS block against the LLM endpoint) transparently falls back to it.
let backendAvailable = false;
export function setBackendAvailable(v: boolean): void {
  backendAvailable = v;
}

/**
 * Call an OpenAI-compatible /chat/completions endpoint.
 * Returns the full assistant message text. When opts.stream is true and
 * opts.onToken is provided, tokens are delivered incrementally as they arrive.
 * On a direct failure, retries via the server proxy (/api/chat) if available.
 */
export async function chat(cfg: LLMConfig, messages: ChatMessage[], opts: ChatOptions = {}): Promise<string> {
  // NE built-in (and any viaServer config) must go through the server proxy —
  // the browser can't reach the gateway directly (CORS).
  if (cfg.viaServer) {
    if (!backendAvailable) throw new Error("内置模型需要服务端在线，请稍后重试");
    const text = await chatViaServer(cfg, messages, opts);
    if (opts.onToken) opts.onToken(text, text);
    return text;
  }
  try {
    return await chatDirect(cfg, messages, opts);
  } catch (e) {
    if (!backendAvailable) throw e;
    const text = await chatViaServer(cfg, messages, opts);
    if (opts.onToken) opts.onToken(text, text);
    return text;
  }
}

async function chatViaServer(cfg: LLMConfig, messages: ChatMessage[], opts: ChatOptions): Promise<string> {
  const resp = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ llm: cfg, messages, json: Boolean(opts.json) }),
    signal: opts.signal,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data?.error || `服务端错误 ${resp.status}`);
  return (data?.content as string) ?? "";
}

async function chatDirect(cfg: LLMConfig, messages: ChatMessage[], opts: ChatOptions = {}): Promise<string> {
  const body: Record<string, unknown> = {
    model: cfg.model,
    messages,
    temperature: cfg.temperature,
    stream: Boolean(opts.stream),
  };
  if (opts.json) body.response_format = { type: "json_object" };

  const resp = await fetch(endpoint(cfg.baseUrl), {
    method: "POST",
    headers: headers(cfg),
    body: JSON.stringify(body),
    signal: opts.signal,
  });

  if (!resp.ok) {
    const detail = await resp.text().catch(() => "");
    throw new Error(`接口返回 ${resp.status} ${resp.statusText}${detail ? `：${truncate(detail, 500)}` : ""}`);
  }

  if (opts.stream && resp.body) {
    return readStream(resp.body, opts.onToken);
  }

  const data = await resp.json();
  const text: string = data?.choices?.[0]?.message?.content ?? "";
  if (opts.onToken) opts.onToken(text, text);
  return text;
}

async function readStream(stream: ReadableStream<Uint8Array>, onToken?: ChatOptions["onToken"]): Promise<string> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let full = "";

  // SSE frames are separated by a blank line; each frame may hold several `data:` lines.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = indexOfFrameBoundary(buffer)) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep).replace(/^(\r?\n)+/, "");
      const delta = parseFrame(frame);
      if (delta) {
        full += delta;
        onToken?.(delta, full);
      }
    }
  }
  // flush any trailing frame
  const tail = parseFrame(buffer);
  if (tail) {
    full += tail;
    onToken?.(tail, full);
  }
  return full;
}

function indexOfFrameBoundary(s: string): number {
  const a = s.indexOf("\n\n");
  const b = s.indexOf("\r\n\r\n");
  if (a === -1) return b;
  if (b === -1) return a;
  return Math.min(a, b);
}

function parseFrame(frame: string): string {
  let out = "";
  for (const rawLine of frame.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line.startsWith("data:")) continue;
    const payload = line.slice(5).trim();
    if (!payload || payload === "[DONE]") continue;
    try {
      const json = JSON.parse(payload);
      const delta: string = json?.choices?.[0]?.delta?.content ?? json?.choices?.[0]?.message?.content ?? "";
      if (delta) out += delta;
    } catch {
      // partial JSON across frames is rare here because we split on blank-line boundaries; ignore.
    }
  }
  return out;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
